import {
    type FC,
    type ReactNode,
    useContext,
    useMemo,
} from "react";
import { isRecord } from "../../../../types/internal/utility";
import { currency } from "../../../Utilities/Generic/Numbers";
import { type ListColumnMap, ListComponent } from "../../Forms/DragDrop/ComponentBases/ListComponent";
import { DragDropComponentErrorContext } from "../../Forms/DragDrop/DragDropComponent";
import { DragDropDataContext } from "../../Forms/DragDrop/DragDropRenderer";
import { type ComponentOptions } from "../../Forms/DragDrop/DragDropTypes";
import { hasColumnsValue } from "../../Forms/DragDrop/ReorderableTableSettings";
import NoTranslate from "../../NoTranslate";
import { ReceiptAccountContext } from "../Receipt";

function formatCurrency(data: unknown): ReactNode {
    if(typeof data === "number") {
        return (
            <NoTranslate>{currency(data)}</NoTranslate>
        );
    }

    return data as ReactNode;
}

const VALUE_STYLE = {
    width: "6em",
};

export const ReceiptTotalTableComponent: FC<ComponentOptions> = ({
    value,
    style,
}) => {
    const { total } = useContext(DragDropDataContext);

    const account = useContext(ReceiptAccountContext);
    const error = useContext(DragDropComponentErrorContext);

    const columnDefinitions = useMemo<ListColumnMap>((): ListColumnMap => {
        if(account) {
            return {
                total: {
                    format    : formatCurrency,
                    valueStyle: VALUE_STYLE,
                },
                invoiceCount: {
                    valueStyle: VALUE_STYLE,
                },
                outstanding: {
                    format    : formatCurrency,
                    valueStyle: VALUE_STYLE,
                },
                change: {
                    format    : formatCurrency,
                    valueStyle: VALUE_STYLE,
                },
                rounding: {
                    format    : formatCurrency,
                    valueStyle: VALUE_STYLE,
                },
            };
        }

        return {
            rounding: {
                format    : formatCurrency,
                valueStyle: VALUE_STYLE,
            },
            savings: {
                format    : formatCurrency,
                valueStyle: VALUE_STYLE,
            },
            discount: {
                format    : formatCurrency,
                valueStyle: VALUE_STYLE,
            },
            sale: {
                format    : formatCurrency,
                valueStyle: VALUE_STYLE,
            },
            change: {
                format    : formatCurrency,
                valueStyle: VALUE_STYLE,
            },
        };
    }, [ account ]);

    if(!hasColumnsValue(value)) {
        return error;
    }

    if(!isRecord(total)) {
        return error;
    }

    return (
        <ListComponent
            columns={columnDefinitions}
            data={total}
            value={value.columns}
            style={style}
        />
    );
};
